import React, { useState, useEffect } from 'react';
import { platformApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Building2, Plus, CheckCircle, XCircle, Shield, RefreshCw, Pencil, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const SuperAdminDashboard = () => {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyAdmins, setCompanyAdmins] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  
  // Form state for new company
  const [newCompany, setNewCompany] = useState({
    name: '',
    admin_email: '',
    admin_password: '',
    admin_name: '',
  });

  // Form state for editing company
  const [editForm, setEditForm] = useState({
    companyName: '',
    adminId: '',
    adminName: '',
    adminEmail: '',
    adminPassword: '',
  });

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const response = await platformApi.getCompanies();
      // Filter out SYSTEM_DEFAULT company
      const activeCompanies = response.data.filter(c => c.status !== 'system');
      setCompanies(activeCompanies);
    } catch (error) {
      console.error('Failed to fetch companies:', error);
      toast.error('Failed to load companies');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    
    if (!newCompany.name || !newCompany.admin_email || !newCompany.admin_password || !newCompany.admin_name) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      setCreating(true);
      const response = await platformApi.createCompany(newCompany);
      toast.success(`Company "${response.data.company.name}" created successfully`);
      setCreateDialogOpen(false);
      setNewCompany({ name: '', admin_email: '', admin_password: '', admin_name: '' });
      fetchCompanies();
    } catch (error) {
      console.error('Failed to create company:', error);
      toast.error(error.response?.data?.detail || 'Failed to create company');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleCompanyStatus = async (company) => {
    const newStatus = company.status === 'active' ? 'disabled' : 'active';
    try {
      await platformApi.updateCompany(company.id, { status: newStatus });
      toast.success(`Company ${newStatus === 'active' ? 'enabled' : 'disabled'} successfully`);
      fetchCompanies();
    } catch (error) {
      console.error('Failed to update company:', error);
      toast.error(error.response?.data?.detail || 'Failed to update company status');
    }
  };

  const handleOpenEditDialog = async (company) => {
    setSelectedCompany(company);
    setEditForm({
      companyName: company.name,
      adminId: '',
      adminName: '',
      adminEmail: '',
      adminPassword: '',
    });
    
    // Fetch company admins
    try {
      const response = await platformApi.getCompanyAdmins(company.id);
      setCompanyAdmins(response.data);
      if (response.data.length > 0) {
        const admin = response.data[0];
        setEditForm(prev => ({
          ...prev,
          adminId: admin.id,
          adminName: admin.name,
          adminEmail: admin.email,
        }));
      }
    } catch (error) {
      console.error('Failed to fetch admins:', error);
      toast.error('Failed to load admin details');
    }
    
    setEditDialogOpen(true);
  };

  const handleUpdateCompany = async (e) => {
    e.preventDefault();
    
    if (!editForm.companyName.trim()) {
      toast.error('Company name is required');
      return;
    }

    try {
      setUpdating(true);
      
      // Update company name if changed
      if (editForm.companyName !== selectedCompany.name) {
        await platformApi.updateCompany(selectedCompany.id, { name: editForm.companyName });
      }
      
      // Update admin if there are changes
      if (editForm.adminId) {
        const adminUpdates = {};
        const originalAdmin = companyAdmins.find(a => a.id === editForm.adminId);
        
        if (editForm.adminName && editForm.adminName !== originalAdmin?.name) {
          adminUpdates.name = editForm.adminName;
        }
        if (editForm.adminEmail && editForm.adminEmail !== originalAdmin?.email) {
          adminUpdates.email = editForm.adminEmail;
        }
        if (editForm.adminPassword) {
          adminUpdates.password = editForm.adminPassword;
        }
        
        if (Object.keys(adminUpdates).length > 0) {
          await platformApi.updateCompanyAdmin(selectedCompany.id, {
            admin_id: editForm.adminId,
            ...adminUpdates
          });
        }
      }
      
      toast.success('Company updated successfully');
      setEditDialogOpen(false);
      setShowPassword(false);
      fetchCompanies();
    } catch (error) {
      console.error('Failed to update company:', error);
      toast.error(error.response?.data?.detail || 'Failed to update company');
    } finally {
      setUpdating(false);
    }
  };

  const activeCompanies = companies.filter(c => c.status === 'active').length;
  const disabledCompanies = companies.filter(c => c.status === 'disabled').length;

  return (
    <div className="p-6 space-y-6" data-testid="super-admin-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Shield className="w-7 h-7" style={{ color: '#1E2580' }} />
            Platform Administration
          </h1>
          <p className="text-slate-500 mt-1">
            Welcome, {user?.name}. Manage companies and platform settings.
          </p>
        </div>
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-company-btn" style={{ backgroundColor: '#1E2580' }}>
              <Plus className="w-4 h-4 mr-2" />
              Create Company
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Create New Company</DialogTitle>
              <DialogDescription>
                Create a new company and its first admin user. The admin will be able to manage users and data within this company.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateCompany}>
              <div className="grid gap-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="company-name">Company Name</Label>
                  <Input
                    id="company-name"
                    data-testid="input-company-name"
                    placeholder="e.g., Acme Corporation"
                    value={newCompany.name}
                    onChange={(e) => setNewCompany({ ...newCompany, name: e.target.value })}
                  />
                </div>
                <div className="border-t pt-4">
                  <h4 className="font-medium text-sm text-slate-900 mb-3">Initial Admin User</h4>
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label htmlFor="admin-name">Admin Name</Label>
                      <Input
                        id="admin-name"
                        data-testid="input-admin-name"
                        placeholder="e.g., John Smith"
                        value={newCompany.admin_name}
                        onChange={(e) => setNewCompany({ ...newCompany, admin_name: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="admin-email">Admin Email</Label>
                      <Input
                        id="admin-email"
                        data-testid="input-admin-email"
                        type="email"
                        placeholder="e.g., admin@company.com"
                        value={newCompany.admin_email}
                        onChange={(e) => setNewCompany({ ...newCompany, admin_email: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="admin-password">Admin Password</Label>
                      <Input
                        id="admin-password"
                        data-testid="input-admin-password"
                        type="password"
                        placeholder="Enter a secure password"
                        value={newCompany.admin_password}
                        onChange={(e) => setNewCompany({ ...newCompany, admin_password: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={creating} data-testid="submit-create-company" style={{ backgroundColor: '#1E2580' }}>
                  {creating ? 'Creating...' : 'Create Company'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg" style={{ backgroundColor: '#1E258010' }}>
                <Building2 className="w-6 h-6" style={{ color: '#1E2580' }} />
              </div>
              <div>
                <p className="text-2xl font-bold">{companies.length}</p>
                <p className="text-sm text-slate-500">Total Companies</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-green-50">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{activeCompanies}</p>
                <p className="text-sm text-slate-500">Active Companies</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-red-50">
                <XCircle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{disabledCompanies}</p>
                <p className="text-sm text-slate-500">Disabled Companies</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Companies Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>All Companies</CardTitle>
            <CardDescription>
              Manage companies on the TeamSync platform
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={fetchCompanies} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full"></div>
            </div>
          ) : companies.length === 0 ? (
            <div className="text-center py-8">
              <Building2 className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">No companies created yet</p>
              <p className="text-sm text-slate-400">Click "Create Company" to get started</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {companies.map((company) => (
                  <TableRow key={company.id} data-testid={`company-row-${company.id}`}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-slate-400" />
                        {company.name}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={company.status === 'active' ? 'default' : 'secondary'}>
                        {company.status === 'active' ? (
                          <><CheckCircle className="w-3 h-3 mr-1" /> Active</>
                        ) : (
                          <><XCircle className="w-3 h-3 mr-1" /> Disabled</>
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-500">
                      {new Date(company.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleOpenEditDialog(company)}
                          data-testid={`edit-company-${company.id}`}
                        >
                          <Pencil className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant={company.status === 'active' ? 'outline' : 'default'}
                          size="sm"
                          onClick={() => handleToggleCompanyStatus(company)}
                          data-testid={`toggle-company-${company.id}`}
                          className={company.status === 'active' ? 'text-red-600 hover:bg-red-50' : ''}
                        >
                          {company.status === 'active' ? 'Disable' : 'Enable'}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit Company Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={(open) => { setEditDialogOpen(open); if (!open) setShowPassword(false); }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Company</DialogTitle>
            <DialogDescription>
              Update company details and admin credentials. Leave password empty to keep current password.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateCompany}>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-company-name">Company Name</Label>
                <Input
                  id="edit-company-name"
                  data-testid="edit-company-name"
                  value={editForm.companyName}
                  onChange={(e) => setEditForm({ ...editForm, companyName: e.target.value })}
                />
              </div>
              
              {companyAdmins.length > 0 && (
                <div className="border-t pt-4">
                  <h4 className="font-medium text-sm text-slate-900 mb-3">Admin User</h4>
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label htmlFor="edit-admin-name">Admin Name</Label>
                      <Input
                        id="edit-admin-name"
                        data-testid="edit-admin-name"
                        value={editForm.adminName}
                        onChange={(e) => setEditForm({ ...editForm, adminName: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-admin-email">Admin Email</Label>
                      <Input
                        id="edit-admin-email"
                        data-testid="edit-admin-email"
                        type="email"
                        value={editForm.adminEmail}
                        onChange={(e) => setEditForm({ ...editForm, adminEmail: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-admin-password">New Password (optional)</Label>
                      <div className="relative">
                        <Input
                          id="edit-admin-password"
                          data-testid="edit-admin-password"
                          type={showPassword ? "text" : "password"}
                          placeholder="Leave empty to keep current"
                          value={editForm.adminPassword}
                          onChange={(e) => setEditForm({ ...editForm, adminPassword: e.target.value })}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-3"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setEditDialogOpen(false); setShowPassword(false); }}>
                Cancel
              </Button>
              <Button type="submit" disabled={updating} data-testid="submit-edit-company" style={{ backgroundColor: '#1E2580' }}>
                {updating ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Info Card */}
      <Card className="bg-slate-50 border-slate-200">
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <div className="p-2 rounded-lg bg-blue-100">
              <Shield className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-medium text-slate-900">Platform Administrator Access</h3>
              <p className="text-sm text-slate-600 mt-1">
                As a Super Admin, you can create and manage companies but cannot access their business data. 
                Each company's data is strictly isolated and only accessible by their own users.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SuperAdminDashboard;
